import ui from "./ui.js";
import * as THREE from "three";
import loadingManager from "./loader.js";

export default function options(state, modelsLibrary, scene, camera, renderer, lights){
    const {materialRadios, mainRadios} = ui()
    let firstLoadForMaterial = true
    const priceValue = document.querySelector('.price-value')

    const defaultPrice = 5000

    const urlParams = new URLSearchParams(window.location.search);
    const materialParam = urlParams.get('material');

    // ── PBR текстуры для blind slats ─────────────────────────────────
    const textureLoader = new THREE.TextureLoader(loadingManager)

    function loadPBR(albedoPath, normalPath, roughnessPath) {
        const albedo    = textureLoader.load(albedoPath)
        const normal    = textureLoader.load(normalPath)
        const roughness = textureLoader.load(roughnessPath)

        albedo.colorSpace = THREE.SRGBColorSpace

        ;[albedo, normal, roughness].forEach(tex => {
            tex.wrapS = THREE.RepeatWrapping
            tex.wrapT = THREE.RepeatWrapping
            tex.repeat.set(2, 1)
        })

        return { albedo, normal, roughness }
    }

    const slatMaterials = {
        veneerRedDark: loadPBR(
            './image/texture/VeneerRedDark001/VeneerRedDark001_COL_2K_METALNESS.png',
            './image/texture/VeneerRedDark001/VeneerRedDark001_NRM_2K_METALNESS.png',
            './image/texture/VeneerRedDark001/VeneerRedDark001_ROUGHNESS_2K_METALNESS.png'
        ),
        veneerWalnut: loadPBR(
            './image/texture/VeneerWalnutRandomMatched001/VeneerWalnutRandomMatched001_COL_2K_METALNESS.png',
            './image/texture/VeneerWalnutRandomMatched001/VeneerWalnutRandomMatched001_NRM_2K_METALNESS.png',
            './image/texture/VeneerWalnutRandomMatched001/VeneerWalnutRandomMatched001_ROUGHNESS_2K_METALNESS.png'
        ),
        woodFine: loadPBR(
            './image/texture/WoodFine006/WoodFine006_COL_2K.jpg',
            './image/texture/WoodFine006/WoodFine006_NRM_2K.png',
            './image/texture/WoodFine006/WoodFine006_REFL_2K.jpg'
        ),
        woodNewMoon: loadPBR(
            './image/texture/WoodQuarteredNewMoon001/WoodQuarteredNewMoon001_COL_2K.jpg',
            './image/texture/WoodQuarteredNewMoon001/WoodQuarteredNewMoon001_NRM_2K.jpg',
            './image/texture/WoodQuarteredNewMoon001/WoodQuarteredNewMoon001_REFL_2K.jpg'
        ),
    }

    // ── Плавная смена цвета ──────────────────────────────────────────
    const colorAnimations = {}

    function animateColor(matFilter, targetHex) {
        const target = new THREE.Color(targetHex)
        const duration = 200

        const meshes = []
        state.currentModel.traverse(child => {
            if (child.isMesh && matFilter(child)) {
                if (!child.material._cloned) {
                    child.material = child.material.clone()
                    child.material._cloned = true
                }
                meshes.push(child)
            }
        })

        if (meshes.length === 0) return

        const key = meshes[0].material.uuid

        if (colorAnimations[key]) cancelAnimationFrame(colorAnimations[key].raf)

        const anim = { raf: null }
        colorAnimations[key] = anim

        const startColor = meshes[0].material.color.clone()
        const startTime = performance.now()

        function tick() {
            const elapsed = performance.now() - startTime
            const t = Math.min(elapsed / duration, 1)
            const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t

            meshes.forEach(child => {
                child.material.color.copy(startColor).lerp(target, eased)
                child.material.needsUpdate = true
            })

            if (t < 1) anim.raf = requestAnimationFrame(tick)
        }

        anim.raf = requestAnimationFrame(tick)
    }

    // ── Фильтры мешей ────────────────────────────────────────────────
    const filterSlats   = child => child.material.name === 'blind slats'
    const filterHolders = child => child.material.name === 'holders'
    const filterWindow  = child => {
        const matName  = child.material.name
        const meshName = child.name.toLowerCase()
        return matName === 'plastic' || (matName === '' && meshName.startsWith('window'))
    }

    // ── Стандартные параметры материала blind slats ──────────────────
    function applySlatMaterialParams() {
        if (!state.currentModel) return
        state.currentModel.traverse(child => {
            if (child.isMesh && filterSlats(child)) {
                child.material.roughness        = 0.81
                child.material.metalness        = 0
                child.material.envMapIntensity  = 0
                child.material.normalScale.set(3, 0)
                child.material.aoMapIntensity   = 1
                child.material.needsUpdate      = true
            }
        })
    }

    // ── Применение PBR текстуры к blind slats ────────────────────────
    function applySlatTexture(texKey) {
        if (!state.currentModel) return
        const pbr = slatMaterials[texKey]
        if (!pbr) return
        state.currentModel.traverse(child => {
            if (child.isMesh && filterSlats(child)) {
                if (!child.material._cloned) {
                    child.material = child.material.clone()
                    child.material._cloned = true
                }
                child.material.map          = pbr.albedo
                child.material.normalMap    = pbr.normal
                child.material.roughnessMap = pbr.roughness
                child.material.color.set('#ffffff')
                child.material.needsUpdate = true
            }
        })
        applySlatMaterialParams()
    }

    // ── Применение цвета к blind slats (сбрасывает текстуры) ─────────
    function applySlatColor(hexColor) {
        if (!state.currentModel) return
        state.currentModel.traverse(child => {
            if (child.isMesh && filterSlats(child)) {
                if (!child.material._cloned) {
                    child.material = child.material.clone()
                    child.material._cloned = true
                }
                child.material.map          = null
                child.material.normalMap    = null
                child.material.roughnessMap = null
                child.material.needsUpdate  = true
            }
        })
        applySlatMaterialParams()
        animateColor(filterSlats, hexColor)
    }

    function applyHolderColor(hexColor) { if (state.currentModel) animateColor(filterHolders, hexColor) }
    function applyWindowColor(hexColor) { if (state.currentModel) animateColor(filterWindow,  hexColor) }

    // ── Слушатели ────────────────────────────────────────────────────
    document.querySelectorAll('input[name="slat-color"]').forEach(radio => {
        radio.addEventListener('change', e => {
            const val = e.target.value
            if (val.startsWith('#')) {
                applySlatColor(val)
            } else {
                applySlatTexture(val)
            }
        })
    })

    document.querySelectorAll('input[name="holder-color"]').forEach(radio => {
        radio.addEventListener('change', e => applyHolderColor(e.target.value))
    })

    mainRadios.forEach(radio => {
        radio.addEventListener('change', e => applyWindowColor(e.target.value))
    })

    // ── Смена модели ─────────────────────────────────────────────────
    materialRadios.forEach((item) => {
        if (materialParam === item.value && firstLoadForMaterial) {
            item.checked = true
            changeMaterial(materialParam)
            firstLoadForMaterial = false
        }

        item.addEventListener('change', event => {
            changeMaterial(event.target.value)
        })

        function changeMaterial(data) {
            if (!modelsLibrary[data]) return

            calculatePrice()

            const url = new URL(window.location)
            url.searchParams.set('material', data)
            window.history.replaceState({}, '', url)

            let oldRotationY = state.currentModel ? state.currentModel.rotation.y : 0
            if (state.currentModel) scene.remove(state.currentModel)

            state.currentModel = modelsLibrary[data]
            state.currentModel.rotation.y = oldRotationY

            state.currentModel.traverse((child) => {
                if (child.isMesh) {
                    child.castShadow = true
                    child.receiveShadow = true
                }
            })

            scene.add(state.currentModel)
        }
    })

    function calculatePrice() {
        let price = defaultPrice
        materialRadios.forEach((item) => {
            if (item.checked) price += +item.dataset.price
        })
        priceValue.textContent = price
    }

    // ── Применяем начальное состояние при загрузке ───────────────────
    const defaultSlat = document.querySelector('input[name="slat-color"]:checked')
    if (defaultSlat) {
        const val = defaultSlat.value
        if (val.startsWith('#')) {
            applySlatColor(val)
        } else {
            applySlatTexture(val)
        }
    }
}